var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Coaching } from 'shared/assets/images/Coaching';
import { Text, Box, NoData, NoShowIndicator, Spinner, Tag, TagLeftIcon, TagLabel, } from 'shared/components';
import { SafetyPlan } from 'shared/assets/icons';
import { StatusTable, TableCellLink } from 'shared/features/table';
import { noteTypeLabels } from 'constants/noteType';
import { appointmentMethods } from 'constants/appointmentMethod';
import { formatTimezoneAgnosticFullDate, formatFullDateTime, linkToNoteOrPlaceholder, } from 'shared/utils';
import { noneEntered, notApplicable } from 'constants/strings';
import { appointmentStatus } from 'constants/ehrNoteEnums';
import { defaultPageSizeOptions } from 'constants/table';
import { Actions, Subjects } from 'constants/accessControlList';
import { useUserIdFromToken } from 'shared/hooks';
import { useRbac } from 'shared/features/rbac';
import { nonFormNoteSchemas } from 'constants/note-schemas';
import { appointmentKindLabels, notAvailableAppointmentKinds, unopenableAppointmentKinds, } from 'constants/appointmentKind';
import { appointmentMediumLabels } from 'constants/appointmentMedium';
import { AdministrativeRoles } from 'src/constants/user';
import { UnknownProviderName } from './unknownProviderName';
import { UnknownTableValue } from './unknownTableValue';
import { useIsBeforeCompassOnboardDateAndMine, useTableNoteStatus } from '../../hooks';
import { IssueButton } from './issueButton';
import { useFlaggedPastInteractions } from 'shared/hooks';
import { useFlagEnabled } from 'flagging/hooks';
export var InteractionHistoryInfo = function (_a) {
    var disclosure = _a.disclosure, memberId = _a.memberId;
    var _b = useFlaggedPastInteractions(memberId), notesWithPlaceholders = _b.notesWithPlaceholders, isLoading = _b.isLoading, isError = _b.isError, isProviderQueryResultsLoading = _b.isProviderQueryResultsLoading;
    var appointmentIssueFlagEnabled = useFlagEnabled('APPOINTMENT_ISSUE');
    var sortableNoteMethod;
    (function (sortableNoteMethod) {
        sortableNoteMethod[sortableNoteMethod["telehealth"] = 0] = "telehealth";
        sortableNoteMethod[sortableNoteMethod["inPerson"] = 1] = "inPerson";
        sortableNoteMethod[sortableNoteMethod["noneEntered"] = 2] = "noneEntered";
        sortableNoteMethod[sortableNoteMethod["notApplicable"] = 3] = "notApplicable";
        sortableNoteMethod[sortableNoteMethod["unknown"] = 4] = "unknown";
    })(sortableNoteMethod || (sortableNoteMethod = {}));
    //userId matches with ownerId in a note
    var userId = useUserIdFromToken();
    var _c = useTableNoteStatus(), getStatusText = _c.getStatusText, calculateNoteStatus = _c.calculateNoteStatus;
    var canI = useRbac().canI;
    var canViewAllSignedNotes = canI(Actions.View, Subjects.NotesSignedNotes);
    var canViewAllNotes = canI(Actions.View, Subjects.NotesAllNotes);
    var canSubmitIssue = canI(Actions.Edit, Subjects.AppointmentsHideAppointments);
    var isBeforeCompassOnboardDateAndMine = useIsBeforeCompassOnboardDateAndMine();
    var calculateNoteMethod = function (_a) {
        var _b, _c, _d, _e;
        var note = _a.note, appointment = _a.appointment, date = _a.date;
        var isNotApplicable = (_b = note === null || note === void 0 ? void 0 : note.noteSchema) === null || _b === void 0 ? void 0 : _b.includes('Unscheduled');
        var isLegacyImport = (note === null || note === void 0 ? void 0 : note.noteSchema) === nonFormNoteSchemas.legacyImport;
        if (isLegacyImport) {
            return sortableNoteMethod.unknown;
        }
        else if (isNotApplicable) {
            return sortableNoteMethod.notApplicable;
        }
        else if (appointment &&
            (unopenableAppointmentKinds.includes(appointment === null || appointment === void 0 ? void 0 : appointment.kind) ||
                isBeforeCompassOnboardDateAndMine(date, (_d = (_c = appointment.care_provider) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : ''))) {
            if ((appointment === null || appointment === void 0 ? void 0 : appointment.medium) === 'VIDEO' || (appointment === null || appointment === void 0 ? void 0 : appointment.medium) === 'PHONE')
                return sortableNoteMethod.telehealth;
            return (appointment === null || appointment === void 0 ? void 0 : appointment.medium) === 'IN_PERSON'
                ? sortableNoteMethod.inPerson
                : sortableNoteMethod.noneEntered;
        }
        return ((_e = note === null || note === void 0 ? void 0 : note.sessionInfo) === null || _e === void 0 ? void 0 : _e.method)
            ? sortableNoteMethod[note.sessionInfo.method]
            : sortableNoteMethod.noneEntered;
    };
    var getNoteTypeText = function (note, appointment, date) {
        var _a, _b, _c, _d;
        var isLegacyImport = (note === null || note === void 0 ? void 0 : note.noteSchema) === nonFormNoteSchemas.legacyImport;
        if (isLegacyImport) {
            return ((_a = note === null || note === void 0 ? void 0 : note.sessionInfo) === null || _a === void 0 ? void 0 : _a.noteType) ? "Kareo: ".concat(note.sessionInfo.noteType) : 'Kareo Note';
        }
        else if (appointment &&
            (unopenableAppointmentKinds.includes(appointment.kind) ||
                isBeforeCompassOnboardDateAndMine(date, (_c = (_b = appointment.care_provider) === null || _b === void 0 ? void 0 : _b.userId) !== null && _c !== void 0 ? _c : ''))) {
            return appointmentKindLabels[appointment === null || appointment === void 0 ? void 0 : appointment.kind];
        }
        else {
            return ((_d = note === null || note === void 0 ? void 0 : note.sessionInfo) === null || _d === void 0 ? void 0 : _d.noteType) ? noteTypeLabels[note.sessionInfo.noteType] : noneEntered;
        }
    };
    var getMethodText = function (note, appointment, date) {
        var _a, _b, _c, _d;
        var isNotApplicable = (_a = note === null || note === void 0 ? void 0 : note.noteSchema) === null || _a === void 0 ? void 0 : _a.includes('Unscheduled');
        var isLegacyImport = (note === null || note === void 0 ? void 0 : note.noteSchema) === nonFormNoteSchemas.legacyImport;
        if (isLegacyImport) {
            return _jsx(UnknownTableValue, {});
        }
        else if (isNotApplicable) {
            return notApplicable;
        }
        else if (appointment &&
            (unopenableAppointmentKinds.includes(appointment === null || appointment === void 0 ? void 0 : appointment.kind) ||
                isBeforeCompassOnboardDateAndMine(date, (_c = (_b = appointment.care_provider) === null || _b === void 0 ? void 0 : _b.userId) !== null && _c !== void 0 ? _c : ''))) {
            return (appointment === null || appointment === void 0 ? void 0 : appointment.medium) ? appointmentMediumLabels[appointment.medium] : noneEntered;
        }
        return ((_d = note === null || note === void 0 ? void 0 : note.sessionInfo) === null || _d === void 0 ? void 0 : _d.method)
            ? appointmentMethods[note.sessionInfo.method].label
            : noneEntered;
    };
    var cols = __spreadArray([
        {
            Header: 'Caregiver',
            accessor: function (row) { return row === null || row === void 0 ? void 0 : row.provider; },
            Cell: function (_a) {
                var row = _a.row;
                var _b = (row === null || row === void 0 ? void 0 : row.original) || {}, provider = _b.provider, note = _b.note;
                if (!(provider === null || provider === void 0 ? void 0 : provider.first_name) && !(provider === null || provider === void 0 ? void 0 : provider.last_name)) {
                    if ((provider === null || provider === void 0 ? void 0 : provider.userId) && isProviderQueryResultsLoading) {
                        return (_jsx(Box, __assign({ m: 2 }, { children: _jsx(Spinner, {}) })));
                    }
                    else {
                        return (_jsx(Box, __assign({ m: 2, maxWidth: "260px" }, { children: _jsx(UnknownProviderName, { isKareo: (note === null || note === void 0 ? void 0 : note.noteSchema) === nonFormNoteSchemas.legacyImport }) })));
                    }
                }
                return (_jsx(Box, __assign({ m: 2, "data-testid": "caregiverRow", maxWidth: "260px" }, { children: _jsxs(Text, __assign({ display: "inline-flex", alignItems: "center", "data-testid": provider === null || provider === void 0 ? void 0 : provider.first_name }, { children: [provider === null || provider === void 0 ? void 0 : provider.first_name, " ", provider === null || provider === void 0 ? void 0 : provider.last_name] })) })));
            },
        },
        {
            Header: 'Role',
            accessor: function (row) { var _a; return (((_a = row === null || row === void 0 ? void 0 : row.provider) === null || _a === void 0 ? void 0 : _a.roles) ? row.provider.roles[0] : null); },
            Cell: function (_a) {
                var row = _a.row;
                var _b = (row === null || row === void 0 ? void 0 : row.original.provider) || {}, roles = _b.roles, userRoles = _b.userRoles;
                if (!roles && !userRoles) {
                    return _jsx(UnknownTableValue, {});
                }
                if (!roles) {
                    return (_jsx(Text, __assign({ "data-testid": "roles", size: "body1" }, { children: AdministrativeRoles[userRoles.find(function (e) { return e.includes('COMPASS'); })] })));
                }
                return (_jsx(Text, __assign({ "data-testid": "roles", size: "body1" }, { children: roles === null || roles === void 0 ? void 0 : roles.join(', ') })));
            },
        },
        {
            Header: 'Date/Time',
            id: 'dateTime',
            accessor: function (row) { return row === null || row === void 0 ? void 0 : row.date; },
            Cell: function (_a) {
                var _b, _c, _d, _e, _f, _g, _h;
                var row = _a.row;
                var enableSafetyPlan = useFlagEnabled('ENABLE_SAFETY_PLAN');
                var _j = row.original, note = _j.note, appointment = _j.appointment, date = _j.date, provider = _j.provider;
                var showSafetyPlan = !!(note === null || note === void 0 ? void 0 : note.signedAt) && !!((_b = note === null || note === void 0 ? void 0 : note.safetyPlan) === null || _b === void 0 ? void 0 : _b.sharedPlanConfirmation) && enableSafetyPlan;
                var subtitleText = getNoteTypeText(note, appointment, date);
                var status = (_c = note === null || note === void 0 ? void 0 : note.sessionInfo) === null || _c === void 0 ? void 0 : _c.status;
                var ownerId = (_d = note === null || note === void 0 ? void 0 : note.ownerId) !== null && _d !== void 0 ? _d : provider.userId;
                var canUserAccessNotes = (canViewAllNotes && !!note) ||
                    userId === ownerId ||
                    (canViewAllSignedNotes && !!(note === null || note === void 0 ? void 0 : note.signedAt));
                var isLegacyImport = (note === null || note === void 0 ? void 0 : note.noteSchema) === nonFormNoteSchemas.legacyImport;
                var isUnopenable = !!appointment &&
                    (unopenableAppointmentKinds.includes(appointment.kind) ||
                        isBeforeCompassOnboardDateAndMine(date, (_f = (_e = appointment.care_provider) === null || _e === void 0 ? void 0 : _e.userId) !== null && _f !== void 0 ? _f : ''));
                return (_jsxs(Box, __assign({ m: 2 }, { children: [_jsx(TableCellLink, __assign({ "data-testid": "interactionDateTime", to: linkToNoteOrPlaceholder(row.original), disabled: !canUserAccessNotes || isUnopenable, subtitle: subtitleText }, { children: _jsxs(Text, __assign({ "data-testid": subtitleText, display: "inline-flex", alignItems: "center" }, { children: [status && status !== appointmentStatus.completed && !isLegacyImport && (_jsx(NoShowIndicator, { showStatus: status })), isLegacyImport
                                        ? formatTimezoneAgnosticFullDate((_g = row === null || row === void 0 ? void 0 : row.original) === null || _g === void 0 ? void 0 : _g.date, true)
                                        : formatFullDateTime((_h = row === null || row === void 0 ? void 0 : row.original) === null || _h === void 0 ? void 0 : _h.date, true)] })) })), showSafetyPlan && (_jsxs(Tag, __assign({ colorScheme: "negative", variant: "base", mt: 2 }, { children: [_jsx(TagLeftIcon, { as: SafetyPlan }), _jsx(TagLabel, { children: "Safety Plan" })] })))] })));
            },
        },
        {
            Header: 'Method',
            accessor: function (row) { return calculateNoteMethod(row); },
            Cell: function (_a) {
                var row = _a.row;
                var _b = row.original, note = _b.note, appointment = _b.appointment, date = _b.date;
                var method = getMethodText(note, appointment, date);
                if (typeof method === 'string') {
                    return _jsx(Text, __assign({ size: "body1" }, { children: method }));
                }
                return method;
            },
        },
        {
            Header: 'Note status',
            accessor: function (row) { return calculateNoteStatus(row); },
            Cell: function (_a) {
                var _b, _c, _d;
                var row = _a.row;
                var _e = row.original, note = _e.note, appointment = _e.appointment, date = _e.date, overdue = _e.overdue, provider = _e.provider;
                var noteStatus = getStatusText(note, appointment, date, overdue);
                var isStringStatus = typeof noteStatus === 'string';
                var ownerId = (_b = note === null || note === void 0 ? void 0 : note.ownerId) !== null && _b !== void 0 ? _b : provider.userId;
                var canUserAccessNotes = (canViewAllNotes && !!note) ||
                    userId === ownerId ||
                    (canViewAllSignedNotes && !!(note === null || note === void 0 ? void 0 : note.signedAt));
                var notAvailable = !!appointment &&
                    ((notAvailableAppointmentKinds === null || notAvailableAppointmentKinds === void 0 ? void 0 : notAvailableAppointmentKinds.includes(appointment === null || appointment === void 0 ? void 0 : appointment.kind)) ||
                        isBeforeCompassOnboardDateAndMine(date, (_d = (_c = appointment.care_provider) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : ''));
                return (_jsx(Box, __assign({ m: 2 }, { children: isStringStatus ? (_jsx(TableCellLink, __assign({ to: linkToNoteOrPlaceholder(row.original), "data-testid": noteStatus, disabled: !canUserAccessNotes || notAvailable }, { children: noteStatus }))) : (noteStatus) })));
            },
        }
    ], (appointmentIssueFlagEnabled && canSubmitIssue
        ? [
            {
                Header: 'Issue',
                accessor: function (row) { return row === null || row === void 0 ? void 0 : row.issue; },
                Cell: function (_a) {
                    var row = _a.row;
                    var _b = row.original, note = _b.note, appointment = _b.appointment;
                    var isSigned = (note === null || note === void 0 ? void 0 : note.signedAt) || null;
                    if (!isSigned && appointment) {
                        return (_jsx(Box, __assign({ mx: 2.5 }, { children: _jsx(IssueButton, { appointment: appointment, disclosure: disclosure }) })));
                    }
                    return _jsx(_Fragment, {});
                },
            },
        ]
        : []), true);
    return (_jsx(_Fragment, { children: _jsx(StatusTable, { cols: cols, data: notesWithPlaceholders, initialState: { sortBy: [{ id: 'dateTime', desc: true }] }, isLoading: isLoading, isError: isError, noData: _jsx(NoData, { title: "This member has no past interactions to display.", icon: _jsx(Coaching, {}) }), maxWidth: '100%', paginated: true, pageSizeOptions: defaultPageSizeOptions, preservedTableId: "pastInteractions" }) }));
};
